<script>
import { getOrderList, updateOrderStatuses } from '@/api/order.js'
import AdminShell from '@/components/admin/layout/AdminShell.vue'
import AdminFilterBar from '@/components/admin/filter/AdminFilterBar.vue'
import AdminStatusBadge from '@/components/admin/table/AdminStatusBadge.vue'
import AdminEmptyState from '@/components/admin/feedback/AdminEmptyState.vue'
import AdminLoadingState from '@/components/admin/feedback/AdminLoadingState.vue'
import AppPagination from '@/components/common/AppPagination.vue'

const toLocalDateString = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const todayString = () => toLocalDateString(new Date())
const monthAgoString = () => {
  const date = new Date()
  date.setMonth(date.getMonth() - 1)
  return toLocalDateString(date)
}

export default {
  name: 'OrderListPage',
  components: {
    AdminShell,
    AdminFilterBar,
    AdminStatusBadge,
    AdminEmptyState,
    AdminLoadingState,
    AppPagination,
  },
  data() {
    return {
      search: {
        keyword: '',
        orderStatus: '',
        paymentType: '',
        deliveryMethod: '',
        startDate: monthAgoString(),
        endDate: todayString(),
      },
      bulkForm: {
        orderStatus: '',
        memo: '',
      },
      selectedOrderCodes: [],
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      bulkSaving: false,
    }
  },
  computed: {
    filterFields() {
      return [
        { key: 'keyword', type: 'text', label: '검색', placeholder: '주문번호, 회원, 상품명' },
        {
          key: 'orderStatus',
          type: 'select',
          label: '주문상태',
          options: this.orderStatusSearchOptions,
        },
        {
          key: 'paymentType',
          type: 'select',
          label: '결제수단',
          options: [
            { title: '전체', value: '' },
            { title: '계좌이체', value: 'CASH' },
            { title: '카드', value: 'CARD' },
          ],
        },
        {
          key: 'deliveryMethod',
          type: 'select',
          label: '수령방식',
          options: [
            { title: '전체', value: '' },
            { title: '배송수령', value: 'DELIVERY' },
            { title: '방문수령', value: 'PICKUP' },
          ],
        },
        { key: 'startDate', type: 'custom' },
        { key: 'endDate', type: 'custom' },
      ]
    },
    orderStatusSearchOptions() {
      return [{ title: '전체', value: '' }, ...this.orderStatusOptions]
    },
    orderStatusOptions() {
      return [
        { title: '주문완료', value: 'ORDERED' },
        { title: '입금만료', value: 'PAYMENT_EXPIRED' },
        { title: '입금완료', value: 'PAID' },
        { title: '준비중', value: 'PREPARING' },
        { title: '방문수령 준비완료', value: 'READY_FOR_PICKUP' },
        { title: '배송중', value: 'SHIPPING' },
        { title: '배송완료', value: 'DELIVERED' },
        { title: '수령완료', value: 'PICKED_UP' },
        { title: '최종완료', value: 'COMPLETED' },
        { title: '취소', value: 'CANCELED' },
      ]
    },
    statusMap() {
      return {
        ORDERED: { label: '주문완료', tone: 'warning' },
        PAYMENT_EXPIRED: { label: '입금만료', tone: 'default' },
        PAID: { label: '입금완료', tone: 'success' },
        PREPARING: { label: '준비중', tone: 'info' },
        READY_FOR_PICKUP: { label: '수령준비완료', tone: 'info' },
        SHIPPING: { label: '배송중', tone: 'info' },
        DELIVERED: { label: '배송완료', tone: 'success' },
        PICKED_UP: { label: '수령완료', tone: 'success' },
        COMPLETED: { label: '최종완료', tone: 'success' },
        CANCELED: { label: '취소', tone: 'default' },
      }
    },
    paymentStatusMap() {
      return {
        REQUESTED: { label: '입금대기', tone: 'warning' },
        PAID: { label: '입금완료', tone: 'success' },
        FAILED: { label: '실패', tone: 'danger' },
        CANCELED: { label: '취소', tone: 'default' },
        REFUNDED: { label: '환불', tone: 'info' },
      }
    },
    selectableItems() {
      return this.items.filter(item => item.orderStatus !== 'CANCELED')
    },
    allPageSelected() {
      return this.selectableItems.length > 0 && this.selectableItems.every(item => this.selectedOrderCodes.includes(item.orderCode))
    },
    somePageSelected() {
      return this.selectableItems.some(item => this.selectedOrderCodes.includes(item.orderCode)) && !this.allPageSelected
    },
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getOrderList({
          keyword: this.search.keyword || undefined,
          orderStatus: this.search.orderStatus || undefined,
          paymentType: this.search.paymentType || undefined,
          deliveryMethod: this.search.deliveryMethod || undefined,
          startDate: this.search.startDate || undefined,
          endDate: this.search.endDate || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
          this.selectedOrderCodes = this.selectedOrderCodes.filter(code => this.items.some(item => item.orderCode === code))
        }
      } catch (e) {
        console.error('[OrderListPage] fetchList error:', e)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.selectedOrderCodes = []
      this.fetchList()
    },
    handleReset() {
      this.search = {
        keyword: '',
        orderStatus: '',
        paymentType: '',
        deliveryMethod: '',
        startDate: monthAgoString(),
        endDate: todayString(),
      }
      this.page = 1
      this.selectedOrderCodes = []
      this.fetchList()
    },
    onPageChange(page) {
      this.page = page
      this.selectedOrderCodes = []
      this.fetchList()
    },
    onSizeChange(size) {
      this.size = size
      this.page = 1
      this.selectedOrderCodes = []
      this.fetchList()
    },
    isSelected(orderCode) {
      return this.selectedOrderCodes.includes(orderCode)
    },
    toggleOrder(item, checked) {
      if (item.orderStatus === 'CANCELED') return
      if (checked && !this.selectedOrderCodes.includes(item.orderCode)) {
        this.selectedOrderCodes = [...this.selectedOrderCodes, item.orderCode]
      } else if (!checked) {
        this.selectedOrderCodes = this.selectedOrderCodes.filter(code => code !== item.orderCode)
      }
    },
    toggleSelectAll(checked) {
      const pageCodes = this.selectableItems.map(item => item.orderCode)
      if (checked) {
        this.selectedOrderCodes = Array.from(new Set([...this.selectedOrderCodes, ...pageCodes]))
      } else {
        this.selectedOrderCodes = this.selectedOrderCodes.filter(code => !pageCodes.includes(code))
      }
    },
    async applyBulkStatus() {
      if (this.selectedOrderCodes.length === 0) {
        window.alert('주문을 선택해주세요.')
        return
      }
      if (!this.bulkForm.orderStatus) {
        window.alert('변경할 주문상태를 선택해주세요.')
        return
      }
      if (!window.confirm(`선택한 ${this.selectedOrderCodes.length}건의 주문상태를 변경하시겠습니까?`)) return
      this.bulkSaving = true
      try {
        const res = await updateOrderStatuses({
          orderCodes: this.selectedOrderCodes,
          orderStatus: this.bulkForm.orderStatus,
          memo: this.bulkForm.memo || undefined,
        })
        if (res.data?.code === 0) {
          this.selectedOrderCodes = []
          this.bulkForm.memo = ''
          await this.fetchList()
        } else {
          window.alert(res.data?.message || '일괄 상태 변경에 실패했습니다.')
        }
      } catch (e) {
        console.error('[OrderListPage] applyBulkStatus error:', e)
        window.alert('일괄 상태 변경 중 오류가 발생했습니다.')
      } finally {
        this.bulkSaving = false
      }
    },
    formatAmount(value) {
      return `${Number(value || 0).toLocaleString()}원`
    },
    formatDateTime(value) {
      return value ? String(value).substring(0, 16).replace('T', ' ') : '-'
    },
    deliveryLabel(value) {
      return value === 'PICKUP' ? '방문수령' : value === 'DELIVERY' ? '배송수령' : value || '-'
    },
    paymentLabel(value) {
      return value === 'CASH' ? '계좌이체' : value === 'CARD' ? '카드' : value || '-'
    },
    goToDetail(item) {
      if (!item?.orderCode) return
      this.$router.push(`/orders/${item.orderCode}`)
    },
  },
}
</script>

<template>
  <AdminShell title="주문 관리">
    <p class="admin-page-subtitle">본사와 지점별 쇼핑몰 주문을 조회하고 상태를 변경합니다.</p>

    <AdminFilterBar
      :model-value="search"
      :fields="filterFields"
      @update:model-value="search = $event"
      @search="handleSearch"
      @reset="handleReset"
    >
      <template #field-startDate="{ modelValue, setValue }">
        <v-text-field
          label="시작일"
          type="date"
          density="comfortable"
          variant="outlined"
          hide-details
          :model-value="modelValue"
          @update:model-value="setValue"
        />
      </template>
      <template #field-endDate="{ modelValue, setValue }">
        <v-text-field
          label="종료일"
          type="date"
          density="comfortable"
          variant="outlined"
          hide-details
          :model-value="modelValue"
          @update:model-value="setValue"
        />
      </template>
    </AdminFilterBar>

    <div class="admin-page-card pa-4 mt-5">
      <div class="bulk-toolbar">
        <div class="bulk-summary">
          <strong>{{ selectedOrderCodes.length }}</strong>건 선택
          <span class="text-caption text-medium-emphasis ml-2">취소 주문은 일괄 변경에서 제외됩니다.</span>
        </div>
        <v-select
          v-model="bulkForm.orderStatus"
          :items="orderStatusOptions"
          item-title="title"
          item-value="value"
          label="변경할 주문상태"
          density="comfortable"
          variant="outlined"
          hide-details
          class="bulk-status"
        />
        <v-text-field
          v-model="bulkForm.memo"
          label="변경 메모"
          density="comfortable"
          variant="outlined"
          hide-details
          class="bulk-memo"
        />
        <v-btn
          color="primary"
          prepend-icon="mdi-check"
          :loading="bulkSaving"
          :disabled="selectedOrderCodes.length === 0 || !bulkForm.orderStatus"
          @click="applyBulkStatus"
        >
          일괄 변경
        </v-btn>
      </div>
    </div>

    <div class="admin-page-card pa-4 mt-5">
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="text-subtitle-1 font-weight-bold">
          주문 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </div>
      </div>

      <div v-if="loading" class="pa-6">
        <AdminLoadingState text="주문 목록을 불러오는 중..." />
      </div>

      <template v-else>
        <v-table density="compact" class="order-table">
          <thead>
            <tr>
              <th class="text-center select-col">
                <v-checkbox-btn
                  :model-value="allPageSelected"
                  :indeterminate="somePageSelected"
                  density="compact"
                  @update:model-value="toggleSelectAll"
                />
              </th>
              <th class="text-center">결제방법</th>
              <th class="text-center">수령방식</th>
              <th class="text-center">결제상태</th>
              <th class="text-center">주문상태</th>
              <th>상품명</th>
              <th class="text-end">주문금액</th>
              <th>회원</th>
              <th class="text-center">주문일시</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in items"
              :key="item.orderCode"
              class="order-row"
              @click="goToDetail(item)"
            >
              <td class="text-center" @click.stop>
                <v-checkbox-btn
                  :model-value="isSelected(item.orderCode)"
                  :disabled="item.orderStatus === 'CANCELED'"
                  density="compact"
                  @update:model-value="(checked) => toggleOrder(item, checked)"
                />
              </td>
              <td class="text-center">{{ paymentLabel(item.paymentType) }}</td>
              <td class="text-center">
                <v-chip size="x-small" variant="tonal" color="info" label>
                  {{ deliveryLabel(item.deliveryMethod) }}
                </v-chip>
              </td>
              <td class="text-center">
                <AdminStatusBadge :status="item.paymentStatus || '-'" :map="paymentStatusMap" size="x-small" />
              </td>
              <td class="text-center">
                <AdminStatusBadge :status="item.orderStatus || '-'" :map="statusMap" size="x-small" />
                <!-- 회원 카드 결제 취소 요청이 처리를 기다린다(2026-11-02) -->
                <div v-if="item.cancelRequested" class="mt-1">
                  <v-chip size="x-small" color="error" variant="tonal">취소요청</v-chip>
                </div>
              </td>
              <td>
                <div class="font-weight-medium">{{ item.productNames || '-' }}</div>
                <div class="text-caption text-medium-emphasis">{{ item.orderCode }}</div>
              </td>
              <td class="text-end font-weight-bold">{{ formatAmount(item.totalAmount) }}</td>
              <td>
                <div class="font-weight-medium">{{ item.userName || '-' }}</div>
                <div class="text-caption text-medium-emphasis">{{ item.userEmail || item.userCode }}</div>
              </td>
              <td class="text-center text-caption">{{ formatDateTime(item.orderedDt) }}</td>
            </tr>
            <tr v-if="items.length === 0">
              <td colspan="9">
                <AdminEmptyState title="조회된 주문이 없습니다." />
              </td>
            </tr>
          </tbody>
        </v-table>
      </template>

      <div class="mt-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </div>
  </AdminShell>
</template>

<style scoped>
.bulk-toolbar {
  display: grid;
  grid-template-columns: auto minmax(180px, 240px) minmax(220px, 1fr) auto;
  align-items: center;
  gap: 12px;
}

.bulk-summary {
  white-space: nowrap;
}

.order-table {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 10px;
}

.select-col {
  width: 52px;
}

.order-row {
  cursor: pointer;
}

.order-row:hover {
  background: rgba(205, 178, 122, 0.06);
}

@media (max-width: 1100px) {
  .bulk-toolbar {
    grid-template-columns: 1fr;
  }

  .bulk-summary {
    white-space: normal;
  }
}
</style>
